// react components
import React, {
    useEffect,
    useState,
} from 'react'
import {
    useDispatch,
    useSelector,
} from 'react-redux'
import AsyncSelect from 'react-select/async'

// data
import {
    defaultReduxState,
    reduxModalErrorEventHandlerSite,
} from 'data'

// pages
import {
    ErrorHelper825,
    FormLabel825,
    ImageHelper825,
} from 'pages'

// services
import {
    axiosErrorHandlerSite,
    ecSlugifySite,
    getApiUrlSiteV2,
} from 'services'

// props
type SelectListAsyncInputV2825Props = {
    apiUrl: string
    cache?: boolean
    clearable: boolean
    defaultOptions?: boolean
    disabled?: boolean
    error?: string
    helperText?: string
    label?: string
    layout?: string
    multi?: boolean
    name: string
    onChange: any
    selectMutlipleMax?: number
    team?: boolean
    value: any
}

// main
export const SelectListAsyncInputV2825: React.FC<SelectListAsyncInputV2825Props> = React.memo(({
    apiUrl,
    cache = true,
    clearable,
    defaultOptions,
    disabled,
    error,
    helperText,
    label,
    layout,
    multi,
    name,
    onChange,
    selectMutlipleMax,
    team,
    value,
}) => {

    const dispatch = useDispatch()
    const reduxAuth = useSelector((state: defaultReduxState) => state.reduxAuth)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)
    const reduxText = useSelector((state: defaultReduxState) => state.reduxText.data)

    useEffect(() => {
        setNewValue(value || (multi ? [] : ''))
    }, [value])

    const [newValue, setNewValue] = useState<any>(value || (multi ? [] : ''))

    async function getOptions(inputValue: string) {
        if (disabled) return
        let getUrl = getApiUrlSiteV2(apiUrl, reduxModalSite)
        getUrl += getUrl.includes('?') ? `&search=${ecSlugifySite(inputValue)}` : `?search=${ecSlugifySite(inputValue)}`
        if (process.env.NODE_ENV === 'development') console.log(getUrl)
        try {
            const response = await fetch(getUrl, reduxAuth?.axiosConfig)
            const json = await response.json()
            return json.results
        } catch (error) {
            axiosErrorHandlerSite({
                apiUrl: getUrl,
                component: 'SelectListAsyncInputV2825',
                dispatch,
                error,
                reduxAuth,
                reduxModalSite,
                reference: 'getOptions',
            })
        }
    }

    const promiseOptions = (inputValue: string) =>
        new Promise(resolve => {
            setTimeout(() => {
                resolve(getOptions(inputValue))
            }, 100)
        })

    function onInputChange(value: any) {
        try {
            if (value === newValue) return
            if (multi && selectMutlipleMax && value?.length > Number(selectMutlipleMax)) {
                setNewValue(newValue)
                return
            }
            setNewValue(value)
            onChange({
                name: name,
                value: value,
            })
            if (team && value.isNew) {
                onChange({
                    name: 'name',
                    value: value.name,
                })
            }
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'SelectListAsyncInputV2825',
                'onInputChange',
            ))
        }
    }

    const customStyles = {
        clearIndicator: (provided: any, state: any) => ({
            ...provided,
            cursor: 'pointer',
        }),
        control: (provided: any, state: any) => ({
            ...provided,
            background: '#f2f2f2',
            border: '1px solid #e4e4e4',
            borderRadius: '8px',
            ':hover': {
                ...provided[':hover'],
                border: '1px solid #e4e4e4',
                boxShadow: 'none',
            },
            ':focus': {
                ...provided[':focus'],
                boxShadow: '0 0 0 2px #ffffff',
                outline: '1px solid #ffffff',
            },
        }),
        input: (provided: any, state: any) => ({
            ...provided,
            color: '#000000',
        }),
        menu: (provided: any, state: any) => ({
            ...provided,
            background: '#f2f2f2',
            borderRadius: '8px',
            boxShadow: '2px 6px 21px -2px rgba(0,0,0,0.75)',
            zIndex: 9999,
        }),
        menuList: (provided: any, state: any) => ({
            ...provided,
            boxShadow: '0 0 4px 2px rgba(var(--mo-color-mo-color-rgb), 0.70)',
            border: '1px solid var(--mo-color-mo-color)',
            maxHeight: '20vh',
            zIndex: 9999,
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            background: '#f2f2f2',
            borderBottom: '1px solid #e4e4e4',
            color: state.isSelected ? 'var(--mo-color-mo-color)' : '#000000',
            ':active': {
                ...provided[':active'],
                backgroundColor: '#f2f2f2',
                color: 'var(--mo-color-mo-color)',
            },
            ':hover': {
                ...provided[':hover'],
                backgroundColor: 'var(--mo-color-mo-color)',
                color: '#000000',
                cursor: 'pointer',
            },
        }),
        singleValue: (provided: any, state: any) => ({
            ...provided,
            color: '#000000',
        }),
    }

    function profileOptions(option: any) {
        let textToReturn = ''
        if (option.cities?.length > 0) {
            textToReturn += `${option.cities.filter((obj: any) => obj.active).map((val: any) => (val.name)).join(' - ')}`
        }
        if (option.countries?.length > 0) {
            textToReturn += `${textToReturn ? ' - ' : ''}${option.countries.map((val: any) => (val.name)).join(' - ')}`
        }
        return textToReturn
    }

    function formatOptionLabel(option: any) {
        if (option.select_description) {
            return (
                <div>
                    <p>{option.name}</p>
                    <p className='slilcl-option-select-description-825'>{option.select_description}</p>
                </div>
            )
        }
        if (layout) {
            return (
                <div className='select-list-input-layout-custom-label-825'>
                    <div
                        className={`slilcl-lcl-image-wrap${(option.get_image_xs || option.get_image_lq) ? '' : ' zarma'}`}
                        style={{
                            borderRadius: ['profile'].includes(layout) ? '50%' : '0%',
                        }}
                    >
                        {(option.get_image_xs || option.get_image_lq)
                            ? (
                                <ImageHelper825
                                    alt={option.name}
                                    dominant_color={option.get_dominant_color}
                                    src={option.get_image_xs || option.get_image_lq}
                                />
                            ) : (
                                <span className='slilcl-image-zarma-name'>{option.name?.[0]}</span>
                            )}
                    </div>
                    <div className='slilcl-lcl-text-wrap'>
                        <p>{option.name}</p>
                        {layout === 'adherent' && option.parent?.name && (
                            <React.Fragment>
                                <p className='slilcl-lcl-profile-text mo-clamp-web1'>
                                    {option.parent?.name}
                                </p>
                            </React.Fragment>
                        )}
                        {layout !== 'portfolio' && (
                            option.profile && <p>{option.profile.name}</p>
                        )}
                        {layout === 'profile' && (
                            <React.Fragment>
                                <p className='slilcl-lcl-profile-text mo-clamp-web1'>
                                    {profileOptions(option)}
                                </p>
                                {option.profile_created_by && (
                                    <p className='slilcl-lcl-profile-text mo-clamp-web1'>
                                        {option.profile_created_by.name}
                                    </p>
                                )}
                            </React.Fragment>
                        )}
                    </div>
                    {option.select_description && (
                        <p className='slilcl-lcl-profile-text mo-clamp-web1'>
                            {option.select_description}
                        </p>
                    )}
                </div>
            )
        }
        return <p>{option.name}</p>
    }

    if (label) {
        return (
            <div className={`input-wrap-825${disabled ? ' disabled' : ''}`}>
                <div className='input-label-wrap'>
                    <FormLabel825
                        helperText={helperText}
                        label={label}
                        name={name}
                    />
                </div>
                <div className=''>
                    <AsyncSelect
                        cacheOptions={cache}
                        defaultOptions={defaultOptions}
                        formatOptionLabel={formatOptionLabel}
                        getOptionLabel={option => option.name}
                        getOptionValue={option => option.id}
                        isClearable={clearable}
                        isDisabled={disabled}
                        isMulti={multi}
                        //@ts-ignore
                        loadOptions={promiseOptions}
                        menuPlacement='auto'
                        // @ts-ignore
                        // menuPortalTarget={inModal ? document.querySelector('.MuiDialog-root') : undefined} // need to check the value of querySelector
                        noOptionsMessage={(e: { inputValue: string }) => e.inputValue ? reduxText[9754] : reduxText[9755]}
                        onChange={onInputChange}
                        placeholder={reduxText[9755]}
                        styles={customStyles}
                        value={newValue}
                    />
                </div>
                <ErrorHelper825 error={error} />
            </div>
        )
    }

    return (
        <div className=''>
            <AsyncSelect
                cacheOptions={cache}
                defaultOptions={defaultOptions}
                formatOptionLabel={formatOptionLabel}
                getOptionLabel={option => option.name}
                getOptionValue={option => option.id}
                isClearable={clearable}
                isDisabled={disabled}
                isMulti={multi}
                //@ts-ignore
                loadOptions={promiseOptions}
                menuPlacement='auto'
                // @ts-ignore
                // menuPortalTarget={inModal ? document.querySelector('.MuiDialog-root') : undefined} // need to check the value of querySelector
                noOptionsMessage={(e: { inputValue: string }) => e.inputValue ? reduxText[9754] : reduxText[9755]}
                onChange={onInputChange}
                placeholder={reduxText[9755]}
                styles={customStyles}
                value={newValue}
            />
        </div>
    )
})
